import * as ProjectAPI from 'api/project-api';
import { txt } from 'libs/i18n';
import { extractFirstValueByTagName } from 'libs/xml-utils';
import { sleep } from 'libs/utils';
import { savingFailedSignal, setSavingStateSignal, } from '../signalsStore/savingState';
import { transcriptionSourceSignal } from '../signalsStore/transcriptionSource';
const LOOP_TIMEOUT = 0.1;
const MINIMUM_DELAY_FROM_LAST_SAVE = 7000;
const MINIMUM_DELAY_FROM_LAST_ACTIVITY = 2000;
const MINIMUM_DELAY_AFTER_FAIL = 15000;
const SAVE_LOOP_MAX_WAIT_TIME = 60000;
export default class DocumentSaver {
    constructor(project, onProjectUpdated, enqueueProjectUpdate, session, editorController, onLeaveEditorRequested, onNotification, logger) {
        this.handleActivity = () => {
            this.lastEditorActivity = this.getCurrentTime();
            if (this.isWaitingForSave) {
                return;
            }
            this.isWaitingForSave = true;
            void this.enqueueUpload();
        };
        this.cleanup = () => {
            this.logger.info('Cleanup called. Aborting any pending saves.');
            this.isAborted = true;
            this.abortSave();
        };
        this.handleSaveToPartial = (saveToPartial) => {
            this.logger.info('Save target changed. Saving to partial: {saveToPartial}', { saveToPartial });
            this.saveToPartial = saveToPartial;
        };
        this.handleTranscriptionStarted = () => {
            this.editorController.updateEtag(null);
        };
        this.hasUnsavedChanges = () => {
            return this.lastEditorActivity > this.lastSuccessfulSaveStarted;
        };
        this.getDiagnosticState = () => {
            const now = this.getCurrentTime();
            const isBeforeSaveDelayAfterFail = now < this.lastSaveFinished + MINIMUM_DELAY_AFTER_FAIL;
            const nothingToSave = !this.hasUnsavedChanges();
            const timeSinceLastActivity = now - this.lastEditorActivity;
            const timeSinceLastSave = now - this.lastSaveFinished;
            const isIdle = timeSinceLastActivity >= MINIMUM_DELAY_FROM_LAST_ACTIVITY;
            const isPeriodicSave = timeSinceLastSave >= MINIMUM_DELAY_FROM_LAST_SAVE;
            let reason = 'READY';
            if (this.isSaveInProgress) {
                reason = 'ALREADY_SAVING';
            }
            else if (this.failStreak > 10) {
                reason = 'TOO_MANY_FAILS';
            }
            else if (this.failStreak > 0 && isBeforeSaveDelayAfterFail) {
                reason = 'COOLING_DOWN_AFTER_FAIL';
            }
            else if (nothingToSave) {
                reason = 'NO_CHANGES_DETECTED';
            }
            else if (!isIdle && !isPeriodicSave) {
                reason = 'WAITING_FOR_IDLE_OR_PERIODIC';
            }
            return {
                now,
                isSaveInProgress: this.isSaveInProgress,
                isAborted: this.isAborted,
                failStreak: this.failStreak,
                nothingToSave,
                lastEditorActivity: this.lastEditorActivity,
                lastSuccessfulSaveStarted: this.lastSuccessfulSaveStarted,
                lastSaveStarted: this.lastSaveStarted,
                timeSinceLastActivity: now - this.lastEditorActivity,
                timeSinceLastSaveFinished: now - this.lastSaveFinished,
                isBeforeSaveDelayAfterFail,
                reasonWhyWait: reason,
            };
        };
        this.enqueueUpload = async () => {
            this.logger.info('enqueueUpload called, waiting for project update queue...');
            await this.enqueueProjectUpdate(async () => {
                if (!savingFailedSignal.value) {
                    setSavingStateSignal('unsaved');
                }
                const saveLoopStartTime = this.getCurrentTime();
                this.logger.info('Entering save loop. Initial state: {state}', {
                    state: JSON.stringify(this.getDiagnosticState()),
                });
                while (!this.shouldSave()) {
                    if (this.isAborted) {
                        this.logger.warn('Save loop aborted, abandoning save.');
                        throw Error('document saver was aborted, abandoning save');
                    }
                    const timeWaiting = this.getCurrentTime() - saveLoopStartTime;
                    // NOTE: Diagnostic log every 5 seconds
                    if (Math.floor(timeWaiting) % 5000 < LOOP_TIMEOUT * 1000) {
                        this.logger.info('Still waiting in save loop... Time: {timeWaiting}ms, State: {state}', {
                            timeWaiting,
                            state: JSON.stringify(this.getDiagnosticState()),
                        });
                    }
                    // NOTE Sometimes due to network errors the loop is indefinite (shouldSave is false)
                    // and the sleep loop is called forever. To prevent this we add a maximum wait time.
                    if (timeWaiting > SAVE_LOOP_MAX_WAIT_TIME) {
                        this.logger.error('SAVE LOOP TIMEOUT! Detailed diagnostics: {diagnostic}', {
                            diagnostic: JSON.stringify(this.getDiagnosticState()),
                            timeWaiting,
                        });
                        this.isSaveInProgress = false;
                        this.isWaitingForSave = false;
                        setSavingStateSignal('unsaved');
                        this.onNotification({ type: 'save-failed' });
                        throw Error(`Save loop timeout after ${timeWaiting}ms. Reason: ${this.getDiagnosticState().reasonWhyWait}`);
                    }
                    // NOTE: In this case await in loop is exactly what we need.
                    // eslint-disable-next-line no-await-in-loop
                    await sleep(LOOP_TIMEOUT);
                }
                this.logger.info('Conditions met. Proceeding to save. Final wait time: {waitTime}ms', {
                    waitTime: this.getCurrentTime() - saveLoopStartTime,
                });
                this.isWaitingForSave = false;
                await this.orderSave();
                return this.project;
            });
        };
        this.handleSaveFailed = () => {
            this.failStreak += 1;
            this.logger.error('Document save failed. Streak: {failStreak}', { failStreak: this.failStreak });
            this.lastSaveFinished = this.getCurrentTime();
            this.isSaveInProgress = false;
            // Only retry if we haven't exceeded the maximum number of attempts
            if (this.failStreak <= 10) {
                void this.enqueueUpload();
            }
            else {
                this.logger.error('Maximum retry attempts exceeded. Not retrying automatically.');
            }
        };
        this.handleSaved = () => {
            const duration = this.getCurrentTime() - this.lastSaveStarted;
            this.lastSaveFinished = this.getCurrentTime();
            this.lastSuccessfulSaveStarted = this.lastSaveStarted;
            this.failStreak = 0;
            this.isSaveInProgress = false;
            this.logger.info('Document saved successfully in {duration}ms.', { duration });
        };
        this.abortSave = () => {
            this.logger.warn('Aborting current save logic.');
            const now = this.getCurrentTime();
            this.failStreak = 0;
            // This resets the scheduler so that we can attempt again if the user continues with editation
            this.lastSaveStarted = now;
            this.lastSaveFinished = now;
            this.isSaveInProgress = false;
            this.isWaitingForSave = false;
        };
        this.handleImportTheirTrsx = (theirTrsx, serverProject) => {
            var _a;
            const language = ProjectAPI.getLanguageFromTrsx(theirTrsx.content);
            this.logger.info('Conflict resolved. Importing their TRSX from server with etag {etag} and language {language}.', { etag: theirTrsx.etag, language: (_a = language === null || language === void 0 ? void 0 : language.code) !== null && _a !== void 0 ? _a : 'unknown' });
            this.editorController.importTrsx({
                content: theirTrsx.content, etag: theirTrsx.etag, label: null, language,
            }, false, serverProject, false);
            setSavingStateSignal('saved');
            this.failStreak = 0;
        };
        this.resolveConflictingTrsx = async () => {
            this.logger.warn('Conflicting TRSX detected. Attempting to resolve.');
            const serverProject = await ProjectAPI.fetchProject(this.session.connection, this.project.id);
            if (serverProject === 'not-found') {
                this.logger.error('Cannot resolve conflict: project not found on server. Forcing reload.');
                this.handleEditorReload();
                return;
            }
            const theirTrsx = await ProjectAPI.fetchTrsx(this.session.connection, serverProject.id, this.saveToPartial ? 'partialTrsx' : 'currentTrsx');
            if (this.saveToPartial && theirTrsx.isFail() && theirTrsx.err() === 'not-found') {
                this.logger.warn('Conflict resolution: partialTrsx not found on server. Resetting etag and retrying partial save.');
                this.editorController.updateEtag(null);
                this.failStreak += 1;
                this.lastSaveFinished = this.getCurrentTime();
                this.isSaveInProgress = false;
                this.isWaitingForSave = false;
                void this.enqueueUpload();
                return;
            }
            if (theirTrsx.isSuccess() && serverProject.processingState !== 'InProgress') {
                this.logger.info('someone edited');
                const lastEditorUsername = extractFirstValueByTagName(theirTrsx.get().content, 'LastEditorUserName');
                this.logger.warn('Project was edited by another user: {lastEditorUsername}. Displaying notification.', { lastEditorUsername: lastEditorUsername !== null && lastEditorUsername !== void 0 ? lastEditorUsername : 'unknown' });
                this.onNotification({
                    type: 'someone-edited',
                    details: lastEditorUsername !== null && lastEditorUsername !== void 0 ? lastEditorUsername : undefined,
                }, this.editorController.exportDocument(true), undefined, () => this.handleImportTheirTrsx(theirTrsx.get(), serverProject));
                const currentPlaybackTime = this.editorController.playback.time;
                this.editorController.playback.seekTo(currentPlaybackTime);
                void this.editorController.syncTextHighlightWithPlayback(true);
                this.isWaitingForSave = false;
                await this.onProjectUpdated(serverProject);
            }
            else {
                this.logger.error('Cannot resolve conflict gracefully. Forcing editor reload.');
                this.handleEditorReload();
            }
        };
        this.getCurrentTime = () => (new Date()).getTime();
        this.shouldSave = () => {
            const now = this.getCurrentTime();
            if (this.isSaveInProgress) {
                // never start a new upload while previous is still running
                return false;
            }
            if (!this.hasUnsavedChanges()) {
                this.logger.info('shouldSave: NO - All changes are already on server.');
                return false;
            }
            if (this.failStreak > 10) {
                this.logger.error('Abandoning save, because we failed too many times ({failStreak}).', { failStreak: this.failStreak });
                this.onNotification({ type: 'save-failed' });
                this.abortSave();
                return false;
            }
            if (this.failStreak > 0) {
                // NOTE: Fixes old bug, we need to wait after unsuccessful save to prevent network clogging.
                const isBeforeSaveDelayAfterFail = now < this.lastSaveFinished + MINIMUM_DELAY_AFTER_FAIL;
                if (isBeforeSaveDelayAfterFail) {
                    return false;
                }
                this.logger.info('Retrying save after {failStreak} unsuccessful attempt(s).', { failStreak: this.failStreak });
                return true;
            }
            const timeSinceLastActivity = now - this.lastEditorActivity;
            const timeSinceLastSave = now - this.lastSaveFinished;
            const isIdle = timeSinceLastActivity >= MINIMUM_DELAY_FROM_LAST_ACTIVITY;
            const isPeriodicSave = timeSinceLastSave >= MINIMUM_DELAY_FROM_LAST_SAVE;
            if (isIdle) {
                this.logger.info('shouldSave: YES - User is idle for {timeSinceLastActivity}ms.', { timeSinceLastActivity });
                return true;
            }
            if (isPeriodicSave && timeSinceLastActivity < MINIMUM_DELAY_FROM_LAST_ACTIVITY) {
                this.logger.info('shouldSave: YES - Periodic save triggered after {timeSinceLastSave}ms.', { timeSinceLastSave });
                return true;
            }
            return false;
        };
        this.shouldIncludeTrsx = () => {
            return this.hasUnsavedChanges();
        };
        this.orderSave = async () => {
            try {
                this.isSaveInProgress = true;
                const shouldIncludeTrsx = this.shouldIncludeTrsx();
                this.lastSaveStarted = this.getCurrentTime();
                this.logger.info('Ordering save. Should include TRSX data: {shouldIncludeTrsx}', { shouldIncludeTrsx });
                if (shouldIncludeTrsx) {
                    await this.saveProject();
                }
                else {
                    this.isSaveInProgress = false;
                }
            }
            catch (e) {
                this.logger.error('Critical error in orderSave function: {error.message}', { error: String(e) }, e);
            }
            finally {
                this.isSaveInProgress = false;
            }
        };
        this.saveProject = async () => {
            var _a, _b, _c;
            try {
                setSavingStateSignal('saving');
                this.logger.info('Saving TRSX');
                let trsx = null;
                let etag = null;
                let label = null;
                let language = null;
                try {
                    const exporter = this.editorController.exportDocument(transcriptionSourceSignal.peek() !== 'mic');
                    ({
                        content: trsx, etag, label, language,
                    } = exporter.exportTRSX());
                }
                catch (error) {
                    // retrying export would not help. The document is broken.
                    this.logger.error('Failed to export TRSX from editor: {error.message}', { error: String(error) }, error);
                    this.abortSave();
                    this.onNotification({
                        type: 'export-failed',
                        // @ts-ignore
                        details: (_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : '',
                    });
                    setSavingStateSignal('failed-no-retry');
                    return;
                }
                const uploadLabel = this.saveToPartial ? null : label;
                this.logger.info('Uploading TRSX to server. Target: {target}, Etag: {etag}, Length: {trsxLength}, Language: {language}, Label: {label}', {
                    target: this.saveToPartial ? 'partialTrsx' : 'currentTrsx',
                    language: (_b = language === null || language === void 0 ? void 0 : language.code) !== null && _b !== void 0 ? _b : 'unknown',
                    etag,
                    label: uploadLabel,
                    trsxLength: trsx.length,
                });
                const result = await ProjectAPI.uploadTrsx(this.session.connection, this.project, trsx, this.saveToPartial
                    ? 'partialTrsx'
                    : 'currentTrsx', (_c = language === null || language === void 0 ? void 0 : language.code) !== null && _c !== void 0 ? _c : 'en-US', etag, uploadLabel);
                if (result.isFail()) {
                    const errorType = result.err();
                    this.logger.error('TRSX upload failed. Server responded with: {errorType}', { errorType });
                    setSavingStateSignal('failed-no-retry');
                    this.failStreak += 1;
                    this.lastSaveFinished = this.getCurrentTime();
                    this.isSaveInProgress = false;
                    if (result.err() === 'conflict') {
                        await this.resolveConflictingTrsx();
                    }
                    else {
                        this.onNotification({ type: 'trsx-upload-failed' }, this.editorController.exportDocument(true), this.project.description.name);
                    }
                    return;
                }
                if (result.isSuccess()) {
                    const newEtag = result.get().etag;
                    this.editorController.updateEtag(newEtag);
                    this.logger.info('TRSX upload successful. New etag received: {newEtag}', { newEtag });
                    setSavingStateSignal('saved');
                    await this.updateProjectFromServer();
                    this.handleSaved();
                }
            }
            catch (error) {
                this.logger.error('Unexpected error during saveProject: {error.message}', { error: String(error) }, error);
                await this.handleSaveError(error);
            }
        };
        this.handleSaveError = async (error) => {
            setSavingStateSignal('retrying');
            this.logger.error('Encountered a generic save error: {error.message}', { error: String(error) }, error);
            await this.updateProjectFromServer();
            this.handleSaveFailed();
        };
        this.updateProjectFromServer = async () => {
            this.logger.info('Fetching updated project data from server.');
            const serverProject = await ProjectAPI.fetchProject(this.session.connection, this.project.id);
            if (serverProject === 'not-found') {
                this.logger.error('Could not reload project from server because it was not found.');
                throw new Error('could not reload project');
            }
            this.project = serverProject;
            await this.onProjectUpdated(serverProject);
            this.logger.info('Project data successfully updated from server.');
        };
        const now = this.getCurrentTime();
        this.project = project;
        this.onProjectUpdated = onProjectUpdated;
        this.session = session;
        this.editorController = editorController;
        this.onLeaveEditorRequested = onLeaveEditorRequested;
        this.lastSaveFinished = now;
        this.lastSuccessfulSaveStarted = now;
        this.lastEditorActivity = 0;
        this.failStreak = 0;
        this.lastSaveStarted = now;
        this.isWaitingForSave = false;
        this.isSaveInProgress = false;
        this.isAborted = false;
        this.enqueueProjectUpdate = enqueueProjectUpdate;
        this.onNotification = onNotification;
        this.saveToPartial = false;
        this.logger = logger;
        this.logger.info('DocumentSaver initialized for project {projectName} in {projectState} state.', {
            projectName: this.project.description.name,
            projectState: this.project.processingState,
        });
    }
    update(project, onProjectUpdated, session, editorController, onLeaveEditorRequested) {
        this.project = project;
        this.onProjectUpdated = onProjectUpdated;
        this.session = session;
        this.editorController = editorController;
        this.onLeaveEditorRequested = onLeaveEditorRequested;
    }
    handleEditorReload() {
        this.logger.error('Cannot softly refresh. Reloading editor.');
        // eslint-disable-next-line no-alert
        alert(`${txt('someoneEditedHead')} ${txt('someoneEdited')}`);
        this.onLeaveEditorRequested();
    }
}
